/*******************************************************************************
    plik  : frontend/src/pages/SuccessPage.tsx
    autor : Michal Kolodziejski (2:480/112.10)
    data  : 2025.01.06
    copyright  : PTODT <https://ptodt.org.pl>

    opis  : Success page with download link and support information

    licencja : MIT
*******************************************************************************/

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getJobStatus, formatFileSize, formatDuration } from '../services/api';
import type { ConversionJob } from '../types';

/**
 * @brief Success page component
 * @return Success page JSX element
 */
const SuccessPage: React.FC = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const [job, setJob] = useState<ConversionJob | null>(null);
    const [error, setError] = useState<string | null>(null);

    /**
     * @brief Fetches job details on mount
     */
    useEffect(() => {
        if (!jobId) {
            setError('Brak identyfikatora zadania');
            return;
        }

        const fetchJob = async () => {
            try {
                const jobData = await getJobStatus(jobId);

                if (jobData.status !== 'completed') {
                    navigate(`/progress/${jobId}`);
                    return;
                }

                setJob(jobData);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Nie można pobrać danych zadania');
            }
        };

        fetchJob();
    }, [jobId, navigate]);

    /**
     * @brief Handles returning to upload page
     */
    const handleBack = useCallback(() => {
        navigate('/');
    }, [navigate]);

    /**
     * @brief Calculates processing duration
     */
    const processingDuration = job?.startedAt && job?.completedAt
        ? job.completedAt - job.startedAt
        : 0;

    /**
     * @brief Generates output filename
     */
    const outputFilename = job?.originalFilename.replace(/\.(msa|MSA)$/, '.st') || 'output.st';

    /** @brief Show loading state */
    if (!job && !error) {
        return (
            <div className="success-page">
                <div className="loading" role="status" aria-live="polite">
                    <div className="loading__spinner" aria-hidden="true"></div>
                    <p className="loading__text">Ładowanie...</p>
                </div>
            </div>
        );
    }

    /** @brief Show error state */
    if (error) {
        return (
            <div className="success-page">
                <div className="error-container" role="alert">
                    <h1 className="error-container__title">Błąd</h1>
                    <p className="error-container__message">{error}</p>
                    <button
                        type="button"
                        className="error-container__button"
                        onClick={handleBack}
                    >
                        Powrót do strony głównej
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="success-page">
            <div className="success-card">
                <div className="success-card__header">
                    <span className="success-card__icon" aria-hidden="true">✅</span>
                    <h1 className="success-card__title">Konwersja zakończona!</h1>
                </div>

                <div className="success-card__content">
                    <div className="success-card__info">
                        <div className="success-card__row">
                            <span className="success-card__label">Plik źródłowy:</span>
                            <span className="success-card__value">{job!.originalFilename}</span>
                        </div>
                        <div className="success-card__row">
                            <span className="success-card__label">Rozmiar wejściowy:</span>
                            <span className="success-card__value">{formatFileSize(job!.uploadedFileSize)}</span>
                        </div>
                        <div className="success-card__row">
                            <span className="success-card__label">Plik docelowy:</span>
                            <span className="success-card__value">{outputFilename}</span>
                        </div>
                        <div className="success-card__row">
                            <span className="success-card__label">Parametry dysku:</span>
                            <span className="success-card__value">
                                {job!.totalTracks} ścieżek × {job!.totalSides} strony × {Math.round(job!.totalSectors / job!.totalTracks / job!.totalSides)} sektorów
                            </span>
                        </div>
                        <div className="success-card__row">
                            <span className="success-card__label">Czas konwersji:</span>
                            <span className="success-card__value">{formatDuration(processingDuration)}</span>
                        </div>
                    </div>

                    <a
                        href={job!.downloadUrl || '#'}
                        className="download-button"
                        download={outputFilename}
                        aria-label={`Pobierz ${outputFilename}`}
                    >
                        <span className="download-button__icon" aria-hidden="true">💾</span>
                        Pobierz plik ST
                    </a>

                    <p className="success-card__warning">
                        <span aria-hidden="true">⏰</span>
                        Link do pobrania wygaśnie za 1 godzinę
                    </p>
                </div>
            </div>

            <div className="support-section">
                <h2 className="support-section__title">Potrzebujesz pomocy?</h2>

                <div className="support-grid">
                    <a
                        href="https://ptodt.org.pl/kontakt"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="support-card"
                    >
                        <span className="support-card__icon" aria-hidden="true">📧</span>
                        <h3 className="support-card__title">Kontakt</h3>
                        <p className="support-card__text">
                            Napisz do nas, jeśli masz pytania lub problemy
                        </p>
                    </a>

                    <a
                        href="https://github.com/ptodt"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="support-card"
                    >
                        <span className="support-card__icon" aria-hidden="true">🐙</span>
                        <h3 className="support-card__title">GitHub</h3>
                        <p className="support-card__text">
                            Kod źródłowy i zgłaszanie błędów
                        </p>
                    </a>

                    <a
                        href="https://ptodt.org.pl/wesprzyj"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="support-card support-card--donate"
                    >
                        <span className="support-card__icon" aria-hidden="true">❤️</span>
                        <h3 className="support-card__title">Wesprzyj projekt</h3>
                        <p className="support-card__text">
                            Pomóż nam rozwijać narzędzia dla społeczności retrokomputerowej
                        </p>
                    </a>
                </div>
            </div>

            <div className="actions-section">
                <Link to="/" className="convert-another-button">
                    Konwertuj kolejny plik
                </Link>
            </div>
        </div>
    );
};

export default SuccessPage;
